/**
 * JobProgressContext - Global context for tracking background job progress
 * 
 * Simplified pattern:
 * - "Started" toast: auto-closes after 3 seconds (lightweight initial feedback)
 * - All ongoing/terminal status handled by sidebar badge + toolbar chip (useScriptJobStatus)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './toast-hooks';
import { subscribeToScriptJob } from '../features/sync/ai-api';
import { useAppStore } from '../store/useAppStore';
import { applyScriptImportResults } from '../features/show-structure/utils/applyScriptImportResults';
import { logger } from '@/utils/logger';
import { JobProgressContext } from './job-progress-hooks';
import { logBetaAnalyticsEvent, toDurationBucket, toErrorCategory } from '@/utils/beta-analytics';
import type { ProjectScope } from '../features/sync/project-scope';
import { getBackend } from '@/backend';

function isActiveCloudScope(scope: ProjectScope): boolean {
    const { cloud } = useAppStore.getState();
    return cloud.orgId === scope.orgId && cloud.projectId === scope.projectId;
}

function hasPersistedProductionElements(jobData: Parameters<Parameters<typeof subscribeToScriptJob>[1]>[2]): boolean {
    return jobData?.productionElementPersistence?.status === 'completed' ||
        Boolean(jobData?.persistedProductionElements?.persistedAt);
}

function pipelineAttribute(jobData: Parameters<Parameters<typeof subscribeToScriptJob>[1]>[2]): 'metadata_only' | 'ocr' {
    return jobData?.pipeline === 'ocr' ? 'ocr' : 'metadata_only';
}

function recordScriptImportJobTrace(
    status: 'completed' | 'failed' | 'partial',
    startedAt: number | null,
    completedAt: number,
    importMode: 'replace' | 'append',
    jobData: Parameters<Parameters<typeof subscribeToScriptJob>[1]>[2],
    options: {
        appliedResults?: boolean;
        error?: string;
    } = {},
): void {
    if (startedAt === null) return;

    const metrics: Record<string, number> = status === 'failed'
        ? { error_reported: options.error ? 1 : 0 }
        : {
            applied_results: options.appliedResults ? 1 : 0,
            persisted_elements: hasPersistedProductionElements(jobData) ? 1 : 0,
        };

    getBackend().telemetry?.recordTrace('script_import_job', startedAt, completedAt - startedAt, {
        attributes: {
            import_mode: importMode,
            pipeline: pipelineAttribute(jobData),
            status,
            ...(status === 'failed' ? { error_category: toErrorCategory(options.error) } : {}),
        },
        metrics,
    });
}

export const JobProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    
    const [isTracking, setIsTracking] = useState(false);
    const unsubscribeRef = useRef<(() => void) | null>(null);
    const activeSubscriptionTokenRef = useRef<symbol | null>(null);
    const trackingStartedAtRef = useRef<number | null>(null);

    // Cleanup on unmount
    useEffect(() => () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
            unsubscribeRef.current = null;
            activeSubscriptionTokenRef.current = null;
        }, []);

    const startJobTracking = useCallback((scope: ProjectScope, jobId?: string, sourceFile?: string, importMode: 'replace' | 'append' = 'replace') => {
        // Clean up any existing subscription
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
            activeSubscriptionTokenRef.current = null;
        }

        const activeJobId = jobId ?? sourceFile ?? 'pending-script-import';
        useAppStore.getState().showStructureActions.setActiveJobId(activeJobId);
        setIsTracking(true);
        const subscriptionScope = { ...scope };
        const subscriptionToken = Symbol('script-job-subscription');
        activeSubscriptionTokenRef.current = subscriptionToken;
        trackingStartedAtRef.current = Date.now();

        // Show lightweight "started" toast (auto-closes after 3s)
        showToast('Script analysis started...', 'info', { duration: 3000 });

        // Subscribe to job status updates
        // Ongoing/terminal status is handled by sidebar badge + toolbar chip (useScriptJobStatus)
        let unsubscribe: (() => void) | null = null;
        const isCurrentSubscription = () => activeSubscriptionTokenRef.current === subscriptionToken;
        const cleanupSubscription = (clearActiveJobId: boolean) => {
            const isCurrent = isCurrentSubscription();
            if (unsubscribeRef.current === unsubscribe) {
                unsubscribe?.();
                unsubscribeRef.current = null;
            } else if (isCurrent) {
                unsubscribe?.();
            }
            if (!isCurrent) {
                return;
            }
            activeSubscriptionTokenRef.current = null;
            setIsTracking(false);
            unsubscribeRef.current = null;
            trackingStartedAtRef.current = null;
            if (clearActiveJobId) {
                const { activeJobId: currentActiveJobId, showStructureActions } = useAppStore.getState();
                if (currentActiveJobId === activeJobId) {
                    showStructureActions.setActiveJobId(null);
                }
            }
        };

        unsubscribe = subscribeToScriptJob(subscriptionScope, (status, _error, jobData) => {
            void (async () => {
                const isTerminalStatus = status === 'completed' || status === 'partial' || status === 'failed';
                if (isTerminalStatus && (!isCurrentSubscription() || !isActiveCloudScope(subscriptionScope))) {
                    cleanupSubscription(true);
                    return;
                }

                if (status === 'completed' || status === 'partial') {
                    const startedAt = trackingStartedAtRef.current;

                    // Load results into store
                    let didApply = false;
                    try {
                        didApply = await applyScriptImportResults(subscriptionScope, {
                            importMode,
                            idPrefix: jobId ?? sourceFile ?? crypto.randomUUID(),
                            includeScriptBlocks: jobData?.pipeline !== 'metadata-only',
                            skipProductionElementMerge: hasPersistedProductionElements(jobData),
                            shouldApply: () => isCurrentSubscription() && isActiveCloudScope(subscriptionScope),
                        });
                    } catch (error: unknown) {
                        logger.error('Failed to load script blocks:', error);
                    }

                    if (!didApply) {
                        cleanupSubscription(true);
                        return;
                    }

                    const completedAt = Date.now();
                    recordScriptImportJobTrace(status, startedAt, completedAt, importMode, jobData, {
                        appliedResults: didApply,
                    });

                    logBetaAnalyticsEvent('beta_script_import_completed', {
                        source: jobData?.pipeline === 'ocr' ? 'ocr' : 'metadata_only',
                        duration_bucket: startedAt === null ? 'unknown' : toDurationBucket(completedAt - startedAt),
                    });

                    cleanupSubscription(true);

                    // Navigate to Show Structure so user sees the status chip
                    void navigate('/show-structure');
                    useAppStore.getState().metaActions.setActiveTab('show-structure');

                } else if (status === 'failed') {
                    const completedAt = Date.now();
                    recordScriptImportJobTrace(status, trackingStartedAtRef.current, completedAt, importMode, jobData, {
                        error: _error,
                    });

                    logBetaAnalyticsEvent('beta_script_import_failed', {
                        error_category: toErrorCategory(_error),
                        source: jobData?.pipeline === 'ocr' ? 'ocr' : 'metadata_only',
                    });

                    useAppStore.getState().showStructureActions.setActiveJobId(null);
                    // Cleanup — status chip + sidebar badge will show the error
                    cleanupSubscription(false);
                }
                // 'processing' / 'stage_1' / 'stage_2' — no action needed
            })();
        }, jobId, sourceFile);

        unsubscribeRef.current = unsubscribe;
    }, [showToast, navigate]);

    const stopJobTracking = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        activeSubscriptionTokenRef.current = null;
        setIsTracking(false);
        trackingStartedAtRef.current = null;
    }, []);

    return (
        <JobProgressContext.Provider value={{ startJobTracking, stopJobTracking, isTracking }}>
            {children}
        </JobProgressContext.Provider>
    );
};

